/**
 * Hook que resuelve nombres y emails de staff a partir de user_ids.
 * Consulta la tabla `profiles` (id, name, email) y cachea los resultados.
 * Si se pasa `tenantId`, también intenta el RPC `get_tenant_member_emails`
 * como respaldo para owners que no tienen fila en `profiles`.
 */

import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

interface StaffInfo {
  name: string | null;
  email: string | null;
}

type StaffMap = Map<string, StaffInfo>;

export function useStaffDirectory(userIds: string[], tenantId?: string | null) {
  // Clave estable: mismo conjunto de ids en distinto orden es la MISMA consulta.
  const unique = useMemo(
    () => [...new Set(userIds)].filter(Boolean).sort(),
    [userIds.join(',')], // eslint-disable-line react-hooks/exhaustive-deps
  );

  /**
   * 🚨 Antes esto era `useEffect` + un `Map` en `useState`, y lo usan QUINCE pantallas. El `Map` vivía
   * en la instancia del hook: cada pantalla tenía SU PROPIA copia del directorio y volvía a pedir los
   * mismos usuarios. Volver a una pantalla ya visitada empezaba de cero.
   *
   * Con la caché compartida, dos pantallas que piden el mismo conjunto de ids reutilizan la respuesta
   * y las consultas simultáneas se deduplican.
   *
   * 🪤 La caché es por CONJUNTO de ids, no por usuario. Lo correcto a gran escala sería normalizar por
   * entidad —que pedir el usuario X en cualquier pantalla reutilice a X— pero eso obliga a una query
   * por usuario o a sembrar la caché a mano, y con los volúmenes de acá no paga la complejidad. Queda
   * anotado por si algún día la lista de staff crece.
   */
  const { data } = useQuery({
    queryKey: ['staff-directory', tenantId ?? null, unique],
    enabled: unique.length > 0,
    queryFn: async (): Promise<StaffMap> => {
      const [profilesRes, emailsRpc] = await Promise.all([
        supabase.from('profiles').select('id, name, email').in('id', unique),
        tenantId
          ? supabase
              .rpc('get_tenant_member_emails', { p_tenant_id: tenantId })
              .then(
                (r) => r,
                // El RPC es un RESPALDO para owners sin fila en `profiles`. Si falla, el directorio
                // se arma igual con lo que sí hay: quedarse sin nombres es peor que sin emails.
                () => ({ data: null as Array<{ user_id: string; email: string | null }> | null }),
              )
          : Promise.resolve({ data: null as Array<{ user_id: string; email: string | null }> | null }),
      ]);

      const map: StaffMap = new Map();
      const emailFromRpc = new Map(
        ((emailsRpc.data ?? []) as Array<{ user_id: string; email: string | null }>).map(
          (r) => [r.user_id, r.email],
        ),
      );
      for (const p of (profilesRes.data ?? []) as { id: string; name: string | null; email: string | null }[]) {
        map.set(p.id, { name: p.name, email: p.email ?? emailFromRpc.get(p.id) ?? null });
      }
      for (const id of unique) {
        if (!map.has(id)) {
          const email = emailFromRpc.get(id) ?? null;
          if (email) map.set(id, { name: null, email });
        }
      }
      return map;
    },
  });

  const directory: StaffMap = useMemo(() => data ?? new Map(), [data]);

  /**
   * `loading` se deriva del directorio, no de la query.
   *
   * 🪤 Se conserva a propósito el criterio original: hay ids pedidos que todavía no están resueltos.
   * Usar `isPending` acá reintroduciría el parpadeo de un frame que este hook ya había arreglado —
   * cuando `userIds` pasa de [] a [id1, id2], la query aún no arrancó y la lista se pintaría con
   * «Usuario» antes de mostrar el spinner.
   */
  const loading = useMemo(() => {
    if (unique.length === 0) return false;
    return !unique.every((id) => directory.has(id));
  }, [unique, directory]);

  /** Resuelve un userId a nombre legible. Nunca expone el UUID crudo. */
  const resolve = useCallback((userId: string | null | undefined): string => {
    if (!userId) return '—';
    const info = directory.get(userId);
    if (info?.name?.trim()) return info.name.trim();
    if (info?.email?.trim()) return info.email.trim();
    return 'Usuario';
  }, [directory]);

  return { resolve, directory, loading };
}
